import {chunk} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {BulkAssemblyMethod} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import BulkAssemblyCard, {
  BULK_ASSEMBLY_CARD_WIDTH,
  BulkAssemblyBinProps,
  BulkAssemblyCardProps,
  BulkAssemblyCardType,
  BulkAssemblyConstructsCardError,
  isBinCard,
  BulkAssemblyInputCardProps,
} from 'molbio/bulk-assembly/card/BulkAssemblyCard';
import {NEAR_BLACK} from 'ui-library/Colors';
import DnDCanvas from 'ui-library/drag-and-drop/DnDCanvas';
import {DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import {DND_BLOCK_SPACING} from 'ui-library/drag-and-drop/DnDStyleConstants';
import {HoverPosition} from 'ui-library/drag-and-drop/hooks/useDnDCanvasDragState';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';

const BLOCK_HEIGHT_BY_BULK_ASSEMBLY_METHOD: {
  [method in BulkAssemblyMethod]: number;
} = {
  // The following two methods do not have any bin-level controls.
  // Spacers are the tallest blocks at just over 120 pixels
  [BulkAssemblyMethod.HOMOLOGY]: 128,
  [BulkAssemblyMethod.CONCATENATION]: 128,
  // Fragment bins using PCR as fragment production method are the tallest blocks at 174 pixels
  [BulkAssemblyMethod.GIBSON]: 176,
  // Fragments bins are the tallest blocks at just over 130 pixels
  [BulkAssemblyMethod.GOLDEN_GATE]: 136,
};

function Arrow(): JSX.Element {
  return (
    <span className={css(styles.arrowContainer)}>
      <span className={css(styles.arrowWrapper)}>
        <span className={css(styles.arrow, styles.arrowShaft)} />
        <span className={css(styles.arrow, styles.arrowHead)} />
      </span>
    </span>
  );
}

export interface BulkAssemblyCardLayoutProps {
  cards: Array<BulkAssemblyInputCardProps>;
  onMoveItem: (
    sourceItem: BulkAssemblyCardProps,
    targetItem: BulkAssemblyCardProps,
    hoverPosition?: HoverPosition
  ) => void;
  bulkAssemblyMethod: BulkAssemblyMethod;
  constructBinErrors: Array<BulkAssemblyConstructsCardError>;
  numFilledOutConstructs: number;
}

/**
 * A DnDCanvas-powered presentational component for a set of BulkAssemblyCard.
 *
 * @param props Component props
 * @param props.cards A list of BulkAssemblyBinProps and/or BulkAssemblySpacerProps.
 * @constructor
 */
export default function BulkAssemblyCardLayout({
  cards,
  onMoveItem,
  bulkAssemblyMethod,
  constructBinErrors,
  numFilledOutConstructs,
}: BulkAssemblyCardLayoutProps): JSX.Element {
  const [maxColumnCount, setMaxColumnCount] = useState(1);
  const {ref, width} = useResizeObserver();
  useEffect(() => {
    if (width != null) {
      // We want to set maxColumnCount based on the available space for cards. However, we also want to keep
      // theConstructs card adjacent to the last card if there aren't enough cards to fill a row. So we
      // calculate the available width from the actual width of the container and the space that the
      // Constructs card + arrow would take up.
      const cols = Math.floor((width - ARROWS_AND_CONSTRUCTS_WIDTH - DND_BLOCK_SPACING) / BLOCK_WIDTH);
      // If that maxColumnCount is more than the amount of cards we have, then set it to the number of cards
      // so we don't create invisible placeholders that take up vertical space.
      const newMaxColumnCount = Math.max(1, Math.min(cols, cards.length));
      setMaxColumnCount(newMaxColumnCount);
    }
  }, [width, cards]);

  // Short-circuit if we don't have any cards.
  if (cards.length === 0) {
    return <div />;
  }

  const blockHeight = BLOCK_HEIGHT_BY_BULK_ASSEMBLY_METHOD[bulkAssemblyMethod];

  const arrangedCards: Array<Array<BulkAssemblyCardProps>> = Array.from(chunk(cards, maxColumnCount));

  // Build constructs card from bin card data
  const binCards = cards.filter((card: BulkAssemblyInputCardProps) => isBinCard(card));
  const binColors = binCards.map((card: BulkAssemblyBinProps) => card.color);

  const layoutContainerFlexBasisStyles = StyleSheet.create({
    flexBasis: {
      flexBasis: `${maxColumnCount * BLOCK_WIDTH + DND_BLOCK_SPACING}px`,
    },
  });

  return (
    <div ref={ref} className={css(styles.wrapper)}>
      <div className={css(styles.layoutContainer, layoutContainerFlexBasisStyles.flexBasis)}>
        <DnDCanvas<BulkAssemblyCardProps>
          blockHeight={blockHeight}
          items={arrangedCards}
          maxColumnCount={maxColumnCount}
          minBlockWidth={BULK_ASSEMBLY_CARD_WIDTH}
          onMoveItem={onMoveItem}
          renderItem={(cardProps: BulkAssemblyInputCardProps, dragHandleProps: DnDDragHandleProps) => (
            <BulkAssemblyCard {...cardProps} dragHandleProps={dragHandleProps} />
          )}
        />
      </div>
      <div className={css(styles.arrowAndConstructsContainer)}>
        <Arrow />
        <BulkAssemblyCard
          cardType={BulkAssemblyCardType.CONSTRUCTS}
          colors={binColors}
          numContained={numFilledOutConstructs}
          errors={constructBinErrors}
          // @ts-expect-error: these types have gotten messy
          bulkAssemblyMethod={{bulkAssemblyMethod}}
        />
      </div>
    </div>
  );
}

const ARROW_WIDTH = 28;
const ARROW_MARGIN = 12;
// Extra 1 pixel is because the arrow head line has width.
const ARROWS_AND_CONSTRUCTS_WIDTH = ARROW_WIDTH + ARROW_MARGIN + BULK_ASSEMBLY_CARD_WIDTH + 1;
const BLOCK_WIDTH = DND_BLOCK_SPACING + BULK_ASSEMBLY_CARD_WIDTH;

const styles = StyleSheet.create({
  arrowContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: '16px',
  },
  arrowWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  arrow: {
    borderStyle: 'solid',
    borderColor: NEAR_BLACK,
    marginRight: `-${ARROW_MARGIN}px`,
  },
  arrowShaft: {
    borderWidth: '2px 0px 0px 0px',
    height: '0px',
    width: `${ARROW_WIDTH}px`,
    flex: '0 0 auto',
  },
  arrowHead: {
    borderWidth: '2px 2px 0px 0px',
    transform: 'rotate(45deg)',
    width: '12px',
    height: '12px',
    marginRight: `${ARROW_MARGIN}px`,
    flex: '0 0 auto',
  },
  wrapper: {
    display: 'flex',
  },
  layoutContainer: {
    flexGrow: 0,
    flexShrink: 0,
  },
  arrowAndConstructsContainer: {
    display: 'flex',
    alignSelf: 'flex-start',
    flex: `0 0 ${ARROWS_AND_CONSTRUCTS_WIDTH}`,
    paddingTop: '12px',
  },
});
